import Swiper from "swiper";
import { Navigation, Manipulation } from "swiper/modules";
import {
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	hasClass,
	getHeightWithMargins,
} from "../../common/html";
import { NavigationOptions, SwiperOptions } from "swiper/types";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { debounce } from "../../utils/debounce";

const disposable: IDisposable = compositeDisposable([]);

export interface SliderComponent {
	dispose: () => void;
	sliderEl: HTMLElement;
	slideEls: HTMLElement[];
	addSlide: (index: number, slides: HTMLElement | string | Array<HTMLElement | string>) => void;
}

export const setTitleHeight = (swiper: Swiper) => {
	const slides = swiper.slides.map((slide, i) => swiper.slides[i]);
	let maxTitleHeight = 0;
	let slidesHaveDifferentHeights = false;

	for (let i = 0, l = slides.length; i < l; i++) {
		const slideEl = slides[i] as HTMLElement;
		const sliderTitleEl = expressQuerySelector<HTMLElement>(slideEl, ".technical-slide-title", false);
		if (!sliderTitleEl) continue;

		const titleHeight = getHeightWithMargins(sliderTitleEl);

		if (i == 0) maxTitleHeight = titleHeight;
		if (maxTitleHeight >= titleHeight) continue;

		slidesHaveDifferentHeights = true;
		maxTitleHeight = titleHeight;
	}

	// all slides are the same height, nothing to do
	if (!slidesHaveDifferentHeights) return;

	for (let i = 0, l = slides.length; i < l; i++) {
		const slideEl = slides[i] as HTMLElement;
		const sliderTitleEl = expressQuerySelector<HTMLElement>(slideEl, ".technical-slide-title", false);
		if (!sliderTitleEl) continue;

		sliderTitleEl.style.height = `${maxTitleHeight}px`;
	}
};

function setNavigationButtonHeight(sliderContainer: HTMLElement) {
	if (!sliderContainer) return null;

	const prevButton = expressQuerySelector<HTMLElement>(sliderContainer, ".technical-slider--navigation-prev");
	const nextButton = expressQuerySelector<HTMLElement>(sliderContainer, ".technical-slider--navigation-next");
	if (!prevButton && !nextButton) return;

	const firstImage = expressQuerySelector<HTMLElement>(
		sliderContainer,
		".technical-slider-slides-container .technical-slider-slide picture",
	);
	if (!firstImage) return;

	const sliderTop = sliderContainer.getBoundingClientRect().top;
	const imgTop = firstImage.getBoundingClientRect().top;
	const imgHeight = firstImage.getBoundingClientRect().height;
	const imgRelativeFromTop = imgTop - sliderTop;
	const calculatedTop = imgRelativeFromTop + imgHeight / 2;

	sliderContainer.style.setProperty("--slider-action-top", calculatedTop + "px");
}

function initSliders(): SliderComponent[] {
	const sliderContainers = expressQuerySelectorAll<HTMLElement>(document, ".technical-slider-container");
	return sliderContainers.map((el) => initSlider(el));
}

function getSettings(sliderEl: HTMLElement): SwiperOptions {
	let settings: SwiperOptions = {};

	if (sliderEl.dataset.sliderSettings) {
		settings = JSON.parse(sliderEl.dataset.sliderSettings);
		if (settings) return settings;
	}

	if (sliderEl.dataset.sliderSettingsEl) {
		const settingsEl = expressQuerySelector<HTMLElement>(document, sliderEl.dataset.sliderSettingsEl, false);
		if (settingsEl && settingsEl.dataset.sliderSettings) {
			settings = JSON.parse(settingsEl.dataset.sliderSettings);
			if (settings) return settings;
		}
	}

	return settings;
}

function getMaxSlidesPerView(settings: SwiperOptions): number {
	let max = typeof settings.slidesPerView === "number" ? settings.slidesPerView : 1;

	if (settings.breakpoints) {
		for (const breakpoint of Object.values(settings.breakpoints)) {
			if (typeof breakpoint.slidesPerView === "number" && breakpoint.slidesPerView > max) {
				max = breakpoint.slidesPerView;
			}
		}
	}

	return max;
}

function initSlider(sliderEl: HTMLElement): SliderComponent {
	if (!sliderEl) return;
	if (!hasClass(sliderEl, "swiper")) sliderEl = expressQuerySelector<HTMLElement>(sliderEl, ".swiper");
	if (!sliderEl || hasClass(sliderEl, "swiper-initialized")) return;

	const settings: SwiperOptions = getSettings(sliderEl);

	const navigation = settings.navigation as NavigationOptions;
	if (navigation && navigation.enabled) {
		settings.modules = [Navigation, Manipulation]; // configure Swiper to use modules
	}

	// Swiper's loop mode needs at least twice as many slides as fit on screen (per slidesPerView) to
	// be able to build its duplicated "loop" slides without having to reorder/reuse the real slides.
	// When there aren't enough slides, loop mode ends up rearranging the real slides (e.g. 2,3,0,1)
	// instead of showing them in the intended order, so we disable loop in that case.
	const wantsLoop = !!settings.loop;
	const maxSlidesPerView = Math.ceil(getMaxSlidesPerView(settings));
	let slideCount = expressQuerySelectorAll<HTMLElement>(sliderEl, ".swiper-slide").length;
	if (wantsLoop && slideCount < maxSlidesPerView * 2) settings.loop = false;

	// init Swiper
	const swiper = new Swiper(sliderEl, settings);

	// Swiper's `initialSlide` option is not reliable when `loop` is enabled, because loop mode
	// prepends duplicated slides to the DOM which can shift the slide that ends up marked active.
	// Explicitly move to the intended slide after init to guarantee the correct slide is active.
	if (settings.loop) swiper.slideToLoop(settings.initialSlide ?? 0, 0, false);

	const evl = expressEventListener(
		window,
		"resize",
		debounce(() => setNavigationButtonHeight(sliderEl), 250),
	);
	disposable.add(evl);

	setNavigationButtonHeight(swiper.hostEl);

	setTitleHeight(swiper);

	return {
		dispose: disposable.dispose,
		sliderEl: swiper.hostEl,
		slideEls: swiper.slides.map((slide, i) => swiper.slides[i]),
		addSlide: (index: number, slides: HTMLElement | string | Array<HTMLElement | string>) => {
			swiper.addSlide(index, slides);
			slideCount += Array.isArray(slides) ? slides.length : 1;

			// Now that more slides were added, re-enable loop mode if there are finally enough
			// slides to support it (loop was disabled at init due to an insufficient slide count).
			if (wantsLoop && !swiper.params.loop && slideCount >= maxSlidesPerView) {
				swiper.params.loop = true;
				swiper.loopCreate();
			}

			swiper.update();

			if (swiper.params.loop) swiper.slideToLoop(0, 0, false);
			else swiper.slideTo(0);
		},
	};
}

window["initSlider"] = initSlider;

export { initSliders, initSlider };
